/* eslint-disable camelcase */
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { BillingInfoCard } from './components/BillingInfoCard/BillingInfoCard'
import { NextInvoiceCard } from './components/NextInvoiceCard/NextInvoiceCard'

import styles from './styles.module.scss'

import { BusinessVariant, getCheckoutSessionURL, getPortalURL, SwitchPlanRequest } from '@/api/billing'
import { SeatType } from '@/api/enterprise'
import { BillingProvider } from '@/api/generated'
import { AddBillingInfoButton } from '@/components/AddBillingInfoButton'
import Button from '@/components/Button'
import Checkbox from '@/components/Checkbox'
import ConfirmDialog from '@/components/ConfirmDialog'
import { useToast } from '@/components/Toast/ToastContext'
import { DPA_URL, MSA_URL } from '@/constants/legal'
import {
  BASE_FEATURES,
  BUSINESS_BUNDLE_NOTETAKER_FEATURE,
  BUSINESS_FEATURES,
  BUSINESS_FEATURES_INTRO,
  MODEL_TRAINING_OPT_OUT_FEATURE,
  NOTETAKER_FEATURES,
  ORG_BASE_FEATURES,
  PRO_FEATURES,
} from '@/constants/planFeatures'
import { useAnalytics } from '@/hooks/useAnalytics'
import { usePricing } from '@/hooks/usePricing'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import BillingInfoModal from '@/pages/app/Home/components/BillingInfoModal/BillingInfoModal'
import EnterpriseContactCard, { TALK_TO_SALES_URL } from '@/pages/auth/EnterpriseContactCard'
import PlanCard, {
  ENTERPRISE_PLAN,
  PlanInformation,
  PRO_PLAN,
  STANDALONE_ENTERPRISE_PLAN,
  TEAM_PLAN,
} from '@/pages/auth/PlanCard'
import { AppDispatch } from '@/store'
import {
  getMySeatInfoThunk,
  getNextInvoiceThunk,
  selectCurrentEnterprise,
  selectIsAdmin,
  selectIsNextInvoiceLoading,
  selectNextInvoice,
  selectPaymentSchedule,
  selectPlanType,
  selectSeatInfo,
  switchEnterprisePlanThunk,
} from '@/store/features/user/userSlice'
import { ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES, PaymentSchedule, PlanType, SubscriptionStatus } from '@/types'
import { calculateTrialDays, formatDate } from '@/utils/dates'
import { EventName, PLATFORM_ADMIN_PORTAL } from '@/utils/eventDefinitions'
import { getPaidPlanLabel, getPlanTypeFromProductName, isLowerTierPlan, PLAN_REF } from '@/utils/subscription'

const BillingPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isManageSubscriptionLoading, setManageSubscriptionLoading] = useState(false)
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false)
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType>(PlanType.Pro)
  const [isGrowthConsentOpen, setIsGrowthConsentOpen] = useState(false)
  const [hasAcceptedGrowthTerms, setHasAcceptedGrowthTerms] = useState(false)
  const [isSwitchingToGrowth, setIsSwitchingToGrowth] = useState(false)
  const [isDowngradeOpen, setIsDowngradeOpen] = useState(false)
  const [isDowngrading, setIsDowngrading] = useState(false)
  const enterprise = useSelector(selectCurrentEnterprise)
  const isAdmin = useSelector(selectIsAdmin)
  const dispatch = useDispatch<AppDispatch>()
  const currentPlanType = useSelector(selectPlanType)
  const currentSchedule = useSelector(selectPaymentSchedule)
  const nextInvoice = useSelector(selectNextInvoice)
  const isNextInvoiceLoading = useSelector(selectIsNextInvoiceLoading)
  const seatInfo = useSelector(selectSeatInfo)
  const isImpersonating = !!useSyncExternalStore(subscribeImpersonation, getImpersonation)
  const { updateToast } = useToast()
  const [schedule, setSchedule] = useState<PaymentSchedule>(currentSchedule ?? PaymentSchedule.Annual)
  const currentSeatPlanType = seatInfo?.plan_type ? getPlanTypeFromProductName(seatInfo.plan_type) : null
  const daysLeft = seatInfo?.trial_ends_at ? calculateTrialDays(seatInfo.trial_ends_at) : 0
  const { currency, locale, getBusinessOptionPrice, pricingState } = usePricing()
  const { trackEvent } = useAnalytics()

  const tier = enterprise?.subscription?.tier ?? seatInfo?.tier ?? null
  const [businessVariant, setBusinessVariant] = useState<BusinessVariant>('bundle')

  // The launch gate is catalog presence: the Business experience renders only once the
  // pricing response carries complete prices for both options and both cycles. Absence
  // is the normal pre-launch state and renders today's page unchanged, immediately.
  const businessCatalogReady = useMemo(
    () =>
      getBusinessOptionPrice('bundle', PaymentSchedule.Monthly) !== undefined &&
      getBusinessOptionPrice('bundle', PaymentSchedule.Annual) !== undefined &&
      getBusinessOptionPrice('dictation_only', PaymentSchedule.Monthly) !== undefined &&
      getBusinessOptionPrice('dictation_only', PaymentSchedule.Annual) !== undefined,
    [getBusinessOptionPrice]
  )

  // Fetch next invoice on mount
  useEffect(() => {
    if (isAdmin && enterprise?.subscription) {
      dispatch(getNextInvoiceThunk())
    }
  }, [dispatch, isAdmin, enterprise?.subscription])

  useEffect(() => {
    dispatch(getMySeatInfoThunk())
  }, [dispatch])

  const switchPlan = async (request: SwitchPlanRequest) => {
    const { plan, variant } = request
    try {
      await dispatch(switchEnterprisePlanThunk(request)).unwrap()
      trackEvent?.(EventName.PlanSwitchCompleted, variant ? { plan, variant } : { plan })
      const planLabel = variant ? 'Growth' : plan.includes('BUSINESS') ? PlanType.Enterprise : PlanType.Pro
      updateToast(`Successfully switched to ${planLabel} plan`, 'success')
    } catch (err) {
      console.error('Failed to switch plan', err)
      // unwrap() rejects with the thunk's rejectWithValue payload: the backend 400 detail or the generic message.
      updateToast(typeof err === 'string' ? err : 'Failed to switch plan', 'error')
    }
  }

  const confirmGrowthSwitch = async () => {
    setIsSwitchingToGrowth(true)
    try {
      await switchPlan({
        plan: PLAN_REF[PlanType.Enterprise][schedule],
        variant: businessVariant,
        accepted_terms: true,
      })
    } finally {
      setIsSwitchingToGrowth(false)
      setIsGrowthConsentOpen(false)
    }
  }

  const confirmDowngrade = async () => {
    setIsDowngrading(true)
    try {
      await switchPlan({ plan: PLAN_REF[PlanType.Pro][currentSchedule ?? schedule] })
    } finally {
      setIsDowngrading(false)
      setIsDowngradeOpen(false)
    }
  }

  // Currently not showing handle payment for past due subscriptions
  const handlePayment = async (planId: PlanType) => {
    if (isImpersonating) {
      updateToast('Action unavailable while impersonating another user', 'error')
      return
    }

    const plan = PLAN_REF[planId][schedule]
    const checkoutVariant = businessCatalogReady && planId === PlanType.Enterprise ? businessVariant : undefined

    // If we already have an active subscription, use the switch plan endpoint
    if (hasActiveOrTrialingSubscription) {
      try {
        setIsLoading(true)
        await switchPlan({ plan })
      } finally {
        setIsLoading(false)
      }
    } else {
      // For new subscriptions, use the checkout flow
      try {
        setIsLoading(true)
        const { data } = await getCheckoutSessionURL({
          plan,
          seats: 3,
          add_billing_info: true,
          currency,
          variant: checkoutVariant,
        })
        trackEvent?.(EventName.CheckoutStarted, {
          source: 'settings_billing',
          platform: PLATFORM_ADMIN_PORTAL,
          isTeam: true,
          plan_id: planId,
          schedule,
          variant: checkoutVariant,
        })
        window.location.href = data.url
      } catch (err) {
        console.error('Failed to get session', err)
        updateToast("Couldn't start checkout. Try again.", 'error')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const manageSubscription = async () => {
    if (isImpersonating) {
      updateToast('Action unavailable while impersonating another user', 'error')
      return
    }

    setManageSubscriptionLoading(true)
    try {
      const { data } = await getPortalURL()
      window.open(data.url, '_blank')
      trackEvent?.(EventName.BillingPortalOpened, undefined)
    } catch (error) {
      updateToast('Failed to find your subscription. Please contact support.', 'error')
      console.error('Failed to get portal URL:', error)
    } finally {
      setManageSubscriptionLoading(false)
    }
  }

  const handlePlanCardButton = async (planType: PlanType) => {
    if (isImpersonating) {
      updateToast('Action unavailable while impersonating another user', 'error')
      return
    }

    if (planType === PlanType.Enterprise && showBusinessPurchaseCards) {
      trackEvent?.(EventName.BusinessPlanUpgradeClicked, {
        option: businessVariant,
        cycle: schedule === PaymentSchedule.Annual ? 'annual' : 'monthly',
        platform: PLATFORM_ADMIN_PORTAL,
      })
    }

    // If no subscription exists, open billing modal
    if (!enterprise?.subscription) {
      setSelectedPlanType(planType)
      setIsBillingModalOpen(true)
      return
    }

    // Growth for a live subscription switches in place with no Stripe page, so the MSA and DPA are accepted here.
    if (planType === PlanType.Enterprise && showBusinessPurchaseCards && hasActiveOrTrialingSubscription) {
      setHasAcceptedGrowthTerms(false)
      setIsGrowthConsentOpen(true)
      return
    }

    setIsLoading(true)
    if (enterprise?.subscription?.status === SubscriptionStatus.PastDue) {
      try {
        const { data } = await getPortalURL()
        window.open(data.url, '_blank')
      } catch (error) {
        updateToast('Failed to find your subscription. Please contact support.', 'error')
        console.error('Failed to get portal URL:', error)
      }
    } else {
      await handlePayment(planType)
    }
    setIsLoading(false)
  }

  const getPlanButtonTitle = (planType: PlanType, schedule: PaymentSchedule) => {
    const subscriptionStatus = enterprise?.subscription?.status

    // If subscription is past due, show different messaging
    if (subscriptionStatus === SubscriptionStatus.PastDue) {
      return 'Update Payment'
    }

    if (subscriptionStatus !== SubscriptionStatus.Active && subscriptionStatus !== SubscriptionStatus.Trialing) {
      return 'Get started'
    }

    if (currentPlanType === planType && currentSchedule == schedule) {
      return 'Current plan'
    }

    // The purchase-cards branch fronts a grandfathered Team subscription with the Pro card.
    if (planType === PlanType.Pro && currentPlanType === PlanType.Team && currentSchedule == schedule) {
      return 'Current plan'
    }

    if (planType === PlanType.Enterprise && isLowerTierPlan(currentPlanType)) {
      return 'Upgrade'
    }
    if (schedule === PaymentSchedule.Annual) {
      return `Save with ${PaymentSchedule.Annual}`
    }

    console.error('Invalid plan type or schedule action, should not happen')
    return undefined
  }

  const hasActiveOrPastDueSubscription = ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES.includes(
    enterprise?.subscription?.status ?? ''
  )
  const hasActiveOrTrialingSubscription =
    enterprise?.subscription?.status === SubscriptionStatus.Active ||
    enterprise?.subscription?.status === SubscriptionStatus.Trialing

  // Enterprise's "Everything in Pro, plus:" only makes sense while the Pro card is on screen.
  const showProCard = !hasActiveOrPastDueSubscription || currentPlanType === PlanType.Pro

  // Subscribed self-serve orgs on Pro or legacy Team upgrade to Growth in place via the
  // plan switch; sales-led evidence (tier 'enterprise') keeps today's page exactly.
  const showGrowthUpgradeForSubscribed =
    hasActiveOrPastDueSubscription && isLowerTierPlan(currentPlanType) && tier !== 'enterprise'

  // The Business purchase cards replace today's card set for orgs with nothing to keep,
  // and for subscribed lower-tier self-serve orgs (their path to Growth is the switch);
  // the no-price current-plan view is scoped to self-serve Business orgs only (ruling:
  // sales-led / unknown-tier subscribers keep today's page exactly). It shows no
  // price, so it turns on the tier alone — the catalog is per-currency and a paying Business
  // org whose admin resolves to an unminted currency must never be re-quoted at the legacy rate.
  const showBusinessPurchaseCards =
    businessCatalogReady && (!hasActiveOrPastDueSubscription || showGrowthUpgradeForSubscribed)
  const showBusinessCurrentPlanView = hasActiveOrPastDueSubscription && tier === 'business'
  // Only the self-serve engine can move Growth down to Team; other Growth orgs keep the sales path.
  const canDowngradeToTeam =
    showBusinessCurrentPlanView && enterprise?.subscription?.billingProvider === BillingProvider.SelfServe

  const growthButtonTitle = showBusinessPurchaseCards ? getPlanButtonTitle(PlanType.Enterprise, schedule) : undefined
  const growthCardBuys = showBusinessPurchaseCards && enterprise?.subscription?.status !== SubscriptionStatus.PastDue

  const currentPlanLabel = getPaidPlanLabel(currentPlanType, tier)
  const currentSeatPlanLabel = getPaidPlanLabel(currentSeatPlanType, seatInfo?.tier)

  // The Business card sells the two new options; Enterprise is sales-led contact-only.
  const businessPlan: PlanInformation = useMemo(
    () => ({
      id: PlanType.Enterprise,
      name: 'Growth',
      monthlyType: PlanType.Enterprise,
      annualType: PlanType.Enterprise,
      featureList:
        businessVariant === 'bundle'
          ? [BUSINESS_FEATURES_INTRO, BUSINESS_BUNDLE_NOTETAKER_FEATURE, ...BUSINESS_FEATURES]
          : [BUSINESS_FEATURES_INTRO, ...BUSINESS_FEATURES],
    }),
    [businessVariant]
  )

  // Which Growth option was bought, matched against this currency's catalog; null on a miss.
  const currentBusinessVariant = useMemo((): BusinessVariant | null => {
    const priceId = enterprise?.subscription?.stripePriceId
    const pricing = pricingState.pricing?.pricing
    if (!priceId || !pricing) return null
    const isOption = (option: typeof pricing.business_bundle) =>
      option?.monthly.price_id === priceId || option?.yearly.price_id === priceId
    if (isOption(pricing.business_bundle)) return 'bundle'
    if (isOption(pricing.business_dictation_only)) return 'dictation_only'
    return null
  }, [enterprise?.subscription?.stripePriceId, pricingState.pricing])

  // Current-plan view for subscribed Business orgs: existing customers keep their rate,
  // so no price is shown and nothing is re-quoted. Only a matched bundle lists Notetaker.
  const currentPlanInfo: PlanInformation = useMemo(() => {
    const baseFeatures =
      currentBusinessVariant === 'bundle'
        ? BASE_FEATURES.filter((feature) => feature !== MODEL_TRAINING_OPT_OUT_FEATURE)
        : ORG_BASE_FEATURES
    return {
      id: currentPlanType ?? PlanType.Enterprise,
      name:
        currentBusinessVariant === 'dictation_only' && currentPlanLabel
          ? `${currentPlanLabel}, Dictation only`
          : (currentPlanLabel ?? ''),
      monthlyType: currentPlanType ?? PlanType.Enterprise,
      annualType: currentPlanType ?? PlanType.Enterprise,
      // Dedupe: PRO and BUSINESS both carry "Priority support" by ratified copy.
      featureList: Array.from(new Set([...baseFeatures, ...PRO_FEATURES, ...BUSINESS_FEATURES])),
    }
  }, [currentPlanType, currentPlanLabel, currentBusinessVariant])

  // Dictation-only Growth's upgrade. The backend refuses Growth option switches, so it goes to sales.
  const notetakerUpgradePlan: PlanInformation = {
    id: PlanType.Enterprise,
    name: 'Growth with Notetaker',
    monthlyType: PlanType.Enterprise,
    annualType: PlanType.Enterprise,
    featureList: ['Everything in your plan, plus:', BUSINESS_BUNDLE_NOTETAKER_FEATURE, ...NOTETAKER_FEATURES],
  }

  if (!isAdmin) {
    return <div className={styles.disclaimer}>You must be an admin to view team billing information.</div>
  }

  return (
    <>
      <BillingInfoModal
        isOpen={isBillingModalOpen}
        onClose={() => setIsBillingModalOpen(false)}
        defaultSchedule={schedule}
        planType={selectedPlanType}
        variant={businessCatalogReady && selectedPlanType === PlanType.Enterprise ? businessVariant : undefined}
      />

      <ConfirmDialog
        isOpen={isGrowthConsentOpen}
        title="Upgrade to Growth?"
        message={
          <>
            <dl className={styles.consentSummary}>
              <div>
                <dt>Plan</dt>
                <dd>Growth, {businessVariant === 'bundle' ? 'Dictation and Notetaker' : 'Dictation only'}</dd>
              </div>
              <div>
                <dt>Billing</dt>
                <dd>{schedule}</dd>
              </div>
            </dl>
            <p className={styles.consentMessage}>
              Your plan changes right away. Review the{' '}
              <a href={MSA_URL} target="_blank" rel="noopener noreferrer">
                Master Services Agreement
              </a>{' '}
              and{' '}
              <a href={DPA_URL} target="_blank" rel="noopener noreferrer">
                Data Processing Addendum
              </a>{' '}
              before you upgrade.
            </p>
            <Checkbox
              checked={hasAcceptedGrowthTerms}
              onChange={() => setHasAcceptedGrowthTerms((accepted) => !accepted)}
              label="I agree to the Master Services Agreement and Data Processing Addendum"
              disabled={isSwitchingToGrowth}
            />
          </>
        }
        confirmLabel="Upgrade to Growth"
        confirmType="dark"
        isConfirming={isSwitchingToGrowth}
        isConfirmDisabled={!hasAcceptedGrowthTerms}
        minWidth={520}
        onConfirm={confirmGrowthSwitch}
        onCancel={() => setIsGrowthConsentOpen(false)}
      />

      <ConfirmDialog
        isOpen={isDowngradeOpen}
        title="Switch to Pro?"
        message="Your plan changes right away. Members with a seat move to Pro, which includes Dictation and Notetaker. There's no refund or credit for the rest of this billing period; your next bill is at the Pro price."
        confirmLabel="Switch to Pro"
        confirmType="dark"
        isConfirming={isDowngrading}
        onConfirm={confirmDowngrade}
        onCancel={() => setIsDowngradeOpen(false)}
      />

      <div className={styles.settingList}>
        {/* Past Due Warning Banner */}
        {enterprise?.subscription?.status === SubscriptionStatus.PastDue && (
          <div className={styles.settingSection}>
            <div className={styles.warningBanner}>
              <div className={styles.warningTitle}>⚠️ Payment Past Due</div>
              <div className={styles.warningMessage}>
                Your payment is overdue. You&apos;ll lose access to your {currentPlanLabel} plan after{' '}
                {enterprise.subscription.planEndsAt && formatDate(enterprise.subscription.planEndsAt, locale)}. Please
                update your payment method to maintain access.
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Warning Banner */}
        {enterprise?.subscription?.cancelAtPeriodEnd && hasActiveOrPastDueSubscription && (
          <div className={styles.settingSection}>
            <div className={styles.warningBanner}>
              <div className={styles.warningTitle}>⚠️ Subscription Cancelled</div>
              <div className={styles.warningMessage}>
                Your subscription has been cancelled and will end on{' '}
                {enterprise.subscription.cancelAt
                  ? formatDate(enterprise.subscription.cancelAt, locale)
                  : enterprise.subscription.planEndsAt
                    ? formatDate(enterprise.subscription.planEndsAt, locale)
                    : 'the end of your billing period'}
                . You&apos;ll continue to have access until then.
              </div>
            </div>
          </div>
        )}

        <div className={styles.billingCards}>
          <BillingInfoCard.Root>
            <BillingInfoCard.Header>
              <BillingInfoCard.Title>Current Plan</BillingInfoCard.Title>
            </BillingInfoCard.Header>
            {currentSeatPlanType &&
            seatInfo?.seat_type !== SeatType.TRIAL &&
            currentSchedule &&
            hasActiveOrPastDueSubscription ? (
              <BillingInfoCard.Body>
                You are on the {currentSchedule} {currentPlanLabel} plan
                {enterprise?.subscription?.numSeats ? ` (${enterprise!.subscription.numSeats} active seats)` : ''}
                {enterprise?.subscription?.status === SubscriptionStatus.PastDue && (
                  <span className={styles.pastDueIndicator}> - Payment Past Due</span>
                )}
              </BillingInfoCard.Body>
            ) : seatInfo?.seat_type === SeatType.NONE && !hasActiveOrPastDueSubscription ? (
              <>
                <BillingInfoCard.Subtitle>You are not currently on a paid seat.</BillingInfoCard.Subtitle>
                <BillingInfoCard.Body>
                  Add billing information to gain access to teams and Pro features.
                </BillingInfoCard.Body>
              </>
            ) : (
              <>
                <BillingInfoCard.Subtitle>
                  You are on a {currentSeatPlanLabel} Trial ({daysLeft} days remaining).
                </BillingInfoCard.Subtitle>
                <BillingInfoCard.Body>
                  {seatInfo?.plan_type === 'FLOW_PRO_NO_BILLING' ? (
                    <>
                      Add billing information to keep access to teams and Pro features. Pay $0 today and only get
                      charged when the trial ends.
                    </>
                  ) : (
                    <>You will be upgraded to {currentPlanLabel} plan when the trial ends.</>
                  )}
                </BillingInfoCard.Body>
              </>
            )}
            <BillingInfoCard.CTA>
              {enterprise?.subscription ? (
                <Button
                  title="Manage Subscription"
                  onClick={manageSubscription}
                  type="dark"
                  isLoading={isManageSubscriptionLoading}
                />
              ) : (
                <AddBillingInfoButton title="Add billing info" type="dark" />
              )}
            </BillingInfoCard.CTA>
          </BillingInfoCard.Root>

          {enterprise?.subscription && <NextInvoiceCard nextInvoice={nextInvoice} isLoading={isNextInvoiceLoading} />}
        </div>

        <div className={styles.settingSection}>
          <div className={styles.settingInfo}>
            {currentPlanType !== PlanType.Enterprise ? (
              <>
                <div className={styles.settingTitle}>Choose your plan</div>
                <div className={styles.settingDescription}>Choose the plan that best fits your needs</div>
              </>
            ) : (
              <>
                <div className={styles.settingTitle}>Your plan</div>
                <div className={styles.settingDescription}>See the details of your plan below</div>
              </>
            )}
          </div>

          {/* Only allow switching from monthly to annual, or if subscription is not active (including past_due) */}
          {!showBusinessCurrentPlanView &&
            (currentSchedule === PaymentSchedule.Monthly || !hasActiveOrPastDueSubscription) && (
              <div className={styles.scheduleToggle}>
                <div
                  className={schedule === PaymentSchedule.Monthly ? styles.active : ''}
                  onClick={() => setSchedule(PaymentSchedule.Monthly)}
                >
                  Monthly
                </div>
                <div
                  className={schedule === PaymentSchedule.Annual ? styles.active : ''}
                  onClick={() => setSchedule(PaymentSchedule.Annual)}
                >
                  Annual
                </div>
              </div>
            )}

          {showBusinessCurrentPlanView ? (
            <div className={styles.planList}>
              <PlanCard
                plan={currentPlanInfo}
                schedule={currentSchedule ?? schedule}
                onClick={() => {}}
                hidePrice
                isCurrentPlan
                buttonTitle="Current plan"
              />
              {currentBusinessVariant === 'dictation_only' && (
                <PlanCard
                  plan={notetakerUpgradePlan}
                  schedule={currentSchedule ?? schedule}
                  onClick={() => {
                    trackEvent?.(EventName.TalkToSalesClicked, {
                      location: 'billing_notetaker_upgrade',
                      platform: PLATFORM_ADMIN_PORTAL,
                    })
                    window.open(TALK_TO_SALES_URL, '_blank', 'noopener')
                  }}
                  hidePrice
                  buttonTitle="Add Notetaker"
                />
              )}
              {canDowngradeToTeam && (
                <PlanCard
                  plan={PRO_PLAN}
                  schedule={currentSchedule ?? schedule}
                  onClick={() => {
                    if (isImpersonating) {
                      updateToast('Action unavailable while impersonating another user', 'error')
                      return
                    }
                    setIsDowngradeOpen(true)
                  }}
                  hidePrice
                  buttonTitle="Switch to Pro"
                />
              )}
              <EnterpriseContactCard location="billing_current_plan_upsell" />
            </div>
          ) : showBusinessPurchaseCards ? (
            <div className={styles.planList}>
              {/* A grandfathered Team org has no Team card here — the Pro card fronts its current
                  plan, so hide Pro's price: it isn't what that org pays. */}
              {(showProCard || currentPlanType === PlanType.Team) && (
                <PlanCard
                  plan={PRO_PLAN}
                  schedule={schedule}
                  onClick={handlePlanCardButton}
                  isLoading={currentPlanType !== PlanType.Pro && isLoading}
                  isCurrentPlan={showGrowthUpgradeForSubscribed && currentSchedule === schedule}
                  hidePrice={currentPlanType === PlanType.Team}
                  buttonTitle={getPlanButtonTitle(PlanType.Pro, schedule)}
                  reservePurchaseTermsSlot={growthCardBuys}
                />
              )}
              <PlanCard
                plan={businessPlan}
                schedule={schedule}
                onClick={handlePlanCardButton}
                isLoading={isLoading}
                buttonTitle={growthButtonTitle}
                showPurchaseTerms={growthCardBuys}
                priceOverride={{
                  monthly: getBusinessOptionPrice(businessVariant, PaymentSchedule.Monthly),
                  annualTotal: getBusinessOptionPrice(businessVariant, PaymentSchedule.Annual),
                }}
                variantToggle={{
                  value: businessVariant,
                  onChange: (variant) => {
                    trackEvent?.(EventName.BusinessPlanOptionSelected, {
                      option: variant,
                      cycle: schedule === PaymentSchedule.Annual ? 'annual' : 'monthly',
                      platform: PLATFORM_ADMIN_PORTAL,
                    })
                    setBusinessVariant(variant)
                  },
                }}
              />
              <EnterpriseContactCard
                location="billing_purchase_cards"
                reserveAnnualSlot={schedule === PaymentSchedule.Annual}
                reservePurchaseTermsSlot={growthCardBuys}
              />
            </div>
          ) : (
            <div className={styles.planList}>
              {/* Only advertise Pro to subscribers already on it, or to teams with no active subscription */}
              {showProCard && (
                <PlanCard
                  plan={PRO_PLAN}
                  schedule={schedule}
                  onClick={handlePlanCardButton}
                  isLoading={currentPlanType !== PlanType.Pro && isLoading}
                  isCurrentPlan={
                    currentPlanType === PlanType.Pro && currentSchedule === schedule && hasActiveOrPastDueSubscription
                  }
                  buttonTitle={getPlanButtonTitle(PlanType.Pro, schedule)}
                />
              )}

              {/* Legacy Team plan */}
              {currentPlanType === PlanType.Team && (
                <PlanCard
                  plan={TEAM_PLAN}
                  schedule={schedule}
                  onClick={handlePlanCardButton}
                  isLoading={currentPlanType !== PlanType.Team && isLoading}
                  isCurrentPlan={
                    currentPlanType === PlanType.Team && currentSchedule === schedule && hasActiveOrPastDueSubscription
                  }
                  buttonTitle={getPlanButtonTitle(PlanType.Team, schedule)}
                />
              )}

              <PlanCard
                plan={showProCard ? ENTERPRISE_PLAN : STANDALONE_ENTERPRISE_PLAN}
                schedule={schedule}
                onClick={handlePlanCardButton}
                isLoading={isLoading}
                isCurrentPlan={
                  currentPlanType === PlanType.Enterprise &&
                  currentSchedule === schedule &&
                  hasActiveOrPastDueSubscription
                }
                buttonTitle={getPlanButtonTitle(PlanType.Enterprise, schedule)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default BillingPage
